import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Box, Button, IconButton, Tooltip, Menu, MenuItem, Divider } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import CodeIcon from '@mui/icons-material/Code';
import TitleIcon from '@mui/icons-material/Title';
import VariableIcon from '@mui/icons-material/Functions';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import TuneIcon from '@mui/icons-material/Tune';
import Slider from '@mui/material/Slider';
import { EditorContent, useEditor, BubbleMenu, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import { Node, mergeAttributes } from '@tiptap/core';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import OpenWithIcon from '@mui/icons-material/OpenWith';

interface TiptapEmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  variables?: string[];
  onVariableInsert?: (variable: string) => void;
}

// Custom resizable image extension (community recipe)
const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: 'auto',
        parseHTML: element => element.getAttribute('width') || 'auto',
        renderHTML: attributes => {
          return { width: attributes.width };
        },
      },
      height: {
        default: 'auto',
        parseHTML: element => element.getAttribute('height') || 'auto',
        renderHTML: attributes => {
          return { height: attributes.height };
        },
      },
    };
  },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const img = document.createElement('img');
      img.src = node.attrs.src;
      img.alt = node.attrs.alt || '';
      img.style.width = node.attrs.width || 'auto';
      img.style.height = node.attrs.height || 'auto';
      img.style.maxWidth = '100%';
      img.style.display = 'block';
      img.draggable = false;

      // Wrapper div for resize handles
      const wrapper = document.createElement('span');
      wrapper.contentEditable = 'false';
      wrapper.style.display = 'inline-block';
      wrapper.style.position = 'relative';
      wrapper.appendChild(img);

      // Check if this node is selected using editor's selection
      const isSelected = () => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null) return false;
        const { from, to } = editor.state.selection;
        return pos >= from && pos <= to;
      };

      // Update selection state
      const updateSelection = () => {
        if (isSelected()) {
          wrapper.style.outline = '2px solid #1976d2';
          wrapper.style.outlineOffset = '2px';
        } else {
          wrapper.style.outline = 'none';
        }
      };

      // Initial selection state
      updateSelection();

      // Listen for selection changes
      editor.on('selectionUpdate', updateSelection);

      // Resize handle
      const handle = document.createElement('span');
      handle.style.position = 'absolute';
      handle.style.right = '-12px';
      handle.style.bottom = '-12px';
      handle.style.width = '28px';
      handle.style.height = '28px';
      handle.style.background = '#1976d2';
      handle.style.borderRadius = '50%';
      handle.style.cursor = 'nwse-resize';
      handle.style.zIndex = '10';
      handle.style.display = 'flex';
      handle.style.alignItems = 'center';
      handle.style.justifyContent = 'center';
      handle.title = 'Drag to resize';
      handle.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 20 20 20 20 16"/><line x1="14" y1="20" x2="20" y2="14"/></svg>`;
      wrapper.appendChild(handle);

      let startX = 0;
      let startWidth = 0;
      let startHeight = 0;
      handle.addEventListener('mousedown', (event) => {
        event.preventDefault();
        startX = event.clientX;
        startWidth = img.offsetWidth;
        startHeight = img.offsetHeight;
        document.body.style.userSelect = 'none';
        const onMouseMove = (moveEvent: MouseEvent) => {
          const diff = moveEvent.clientX - startX;
          const newWidth = Math.max(40, startWidth + diff);
          img.style.width = newWidth + 'px';
        };
        const onMouseUp = () => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          document.body.style.userSelect = '';
          // Update node attrs
          editor.commands.command(({ tr }) => {
            tr.setNodeMarkup(getPos(), undefined, {
              ...node.attrs,
              width: img.style.width,
              height: img.style.height,
            });
            return true;
          });
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });
      return {
        dom: wrapper,
        contentDOM: null,
      };
    };
  },
});

// ForwardRef to expose insertImage method
const TiptapEmailEditor = forwardRef<any, TiptapEmailEditorProps>(
  ({ value, onChange, variables = [], onVariableInsert }, ref) => {
    const editor = useEditor({
      extensions: [
        StarterKit,
        Underline,
        Link,
        ResizableImage,
        HorizontalRule,
      ],
      content: value,
      onUpdate: ({ editor }) => {
        onChange(editor.getHTML());
      },
      editorProps: {
        attributes: {
          style: 'min-height: 240px; padding: 16px; font-size: 1rem; background: #fff; border-radius: 8px; border: 1px solid #e0e0e0;',
        },
      },
    });

    // Expose insertImage method to parent
    useImperativeHandle(ref, () => ({
      insertImage: (url: string) => {
        if (editor) {
          editor.chain().focus().setImage({ src: url }).run();
        }
      },
    }), [editor]);

    // Variable menu
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const handleVariableMenu = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleVariableSelect = (variable: string) => {
      if (editor) {
        editor.commands.insertContent(`{{${variable}}}`);
      }
      if (onVariableInsert) onVariableInsert(variable);
      setAnchorEl(null);
    };

    // Alt text popover state
    const [altPopoverAnchor, setAltPopoverAnchor] = React.useState<null | HTMLElement>(null);
    const [altText, setAltText] = React.useState('');

    // Show popover when image is selected
    React.useEffect(() => {
      if (!editor) return;
      const { state } = editor;
      const { selection } = state;
      const node = selection ? selection.$from.node() : null;
      if (editor.isActive('image') && node && node.type.name === 'image') {
        // Find the selected image DOM node
        const dom = document.querySelector('.ProseMirror-selectednode img');
        if (dom) {
          setAltPopoverAnchor(dom as HTMLElement);
          setAltText(editor.getAttributes('image').alt || '');
        }
      } else {
        setAltPopoverAnchor(null);
      }
    }, [editor?.state.selection]);

    const handleAltTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setAltText(e.target.value);
      if (editor) {
        editor.commands.command(({ tr, state }) => {
          const { selection } = state;
          const node = selection ? selection.$from.node() : null;
          if (node && node.type.name === 'image') {
            tr.setNodeMarkup(selection.from, undefined, {
              ...node.attrs,
              alt: e.target.value,
            });
            return true;
          }
          return false;
        });
      }
    };

    // Helper to check if image is selected
    const isImageSelected = editor?.isActive('image');
    const imageAttrs = isImageSelected ? editor?.getAttributes('image') : {};

    return (
      <Box position="relative">
        {/* Alt text popover for images */}
        <Popover
          open={!!altPopoverAnchor}
          anchorEl={altPopoverAnchor}
          onClose={() => setAltPopoverAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          disableRestoreFocus
        >
          <Box p={2}>
            <TextField
              label="Image Alt Text"
              value={altText}
              onChange={handleAltTextChange}
              size="small"
              fullWidth
            />
          </Box>
        </Popover>
        {editor && (
          <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 2, p: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Standard formatting buttons */}
              <Tooltip title="Bold"><IconButton size="small" onClick={() => editor.chain().focus().toggleBold().run()} color={editor.isActive('bold') ? 'primary' : 'default'}><FormatBoldIcon /></IconButton></Tooltip>
              <Tooltip title="Italic"><IconButton size="small" onClick={() => editor.chain().focus().toggleItalic().run()} color={editor.isActive('italic') ? 'primary' : 'default'}><FormatItalicIcon /></IconButton></Tooltip>
              <Tooltip title="Underline"><IconButton size="small" onClick={() => editor.chain().focus().toggleUnderline().run()} color={editor.isActive('underline') ? 'primary' : 'default'}><FormatUnderlinedIcon /></IconButton></Tooltip>
              <Tooltip title="Heading"><IconButton size="small" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}><TitleIcon /></IconButton></Tooltip>
              <Tooltip title="Bullet List"><IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()} color={editor.isActive('bulletList') ? 'primary' : 'default'}><FormatListBulletedIcon /></IconButton></Tooltip>
              <Tooltip title="Numbered List"><IconButton size="small" onClick={() => editor.chain().focus().toggleOrderedList().run()} color={editor.isActive('orderedList') ? 'primary' : 'default'}><FormatListNumberedIcon /></IconButton></Tooltip>
              <Tooltip title="Link"><IconButton size="small" onClick={() => {
                const url = prompt('Enter URL');
                if (url) editor.chain().focus().setLink({ href: url }).run();
              }} color={editor.isActive('link') ? 'primary' : 'default'}><LinkIcon /></IconButton></Tooltip>
              <Tooltip title="Image"><IconButton size="small" onClick={() => {
                const url = prompt('Enter image URL');
                if (url && (editor.chain().focus() as any).setImage) {
                  (editor.chain().focus() as any).setImage({ src: url }).run();
                }
              }}><ImageIcon /></IconButton></Tooltip>
              <Tooltip title="Divider"><IconButton size="small" onClick={() => editor.chain().focus().setHorizontalRule().run()}><HorizontalRuleIcon /></IconButton></Tooltip>
              <Tooltip title="Variable"><IconButton size="small" onClick={handleVariableMenu}><VariableIcon /></IconButton></Tooltip>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {variables.length === 0 && <MenuItem disabled>No variables</MenuItem>}
                {variables.map(v => (
                  <MenuItem key={v} onClick={() => handleVariableSelect(v)}>{`{{${v}}}`}</MenuItem>
                ))}
              </Menu>
              {/* Image controls if image is selected */}
              {isImageSelected && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Tooltip title="Align Left"><IconButton size="small" onClick={() => {
                    editor.chain().focus().command(({ tr, state }) => {
                      const { selection } = state;
                      const node = selection ? selection.$from.node() : null;
                      if (node && node.type.name === 'image') {
                        tr.setNodeMarkup(selection.from, undefined, {
                          ...node.attrs,
                          style: 'display:block;margin:0;float:left;',
                        });
                        return true;
                      }
                      return false;
                    }).run();
                  }} color={imageAttrs.style?.includes('float:left') ? 'primary' : 'default'}><FormatAlignLeftIcon /></IconButton></Tooltip>
                  <Tooltip title="Align Center"><IconButton size="small" onClick={() => {
                    editor.chain().focus().command(({ tr, state }) => {
                      const { selection } = state;
                      const node = selection ? selection.$from.node() : null;
                      if (node && node.type.name === 'image') {
                        tr.setNodeMarkup(selection.from, undefined, {
                          ...node.attrs,
                          style: 'display:block;margin-left:auto;margin-right:auto;float:none;',
                        });
                        return true;
                      }
                      return false;
                    }).run();
                  }} color={imageAttrs.style?.includes('margin-left:auto') ? 'primary' : 'default'}><FormatAlignCenterIcon /></IconButton></Tooltip>
                  <Tooltip title="Align Right"><IconButton size="small" onClick={() => {
                    editor.chain().focus().command(({ tr, state }) => {
                      const { selection } = state;
                      const node = selection ? selection.$from.node() : null;
                      if (node && node.type.name === 'image') {
                        tr.setNodeMarkup(selection.from, undefined, {
                          ...node.attrs,
                          style: 'display:block;margin:0;float:right;',
                        });
                        return true;
                      }
                      return false;
                    }).run();
                  }} color={imageAttrs.style?.includes('float:right') ? 'primary' : 'default'}><FormatAlignRightIcon /></IconButton></Tooltip>
                  <Tooltip title="Width"><TuneIcon fontSize="small" sx={{ ml: 1, mr: 0.5 }} /></Tooltip>
                  <Slider
                    min={40}
                    max={800}
                    value={parseInt(imageAttrs.width) || 300}
                    onChange={(_, v) => {
                      editor.chain().focus().command(({ tr, state }) => {
                        const { selection } = state;
                        const node = selection ? selection.$from.node() : null;
                        if (node && node.type.name === 'image') {
                          tr.setNodeMarkup(selection.from, undefined, {
                            ...node.attrs,
                            width: `${v}px`,
                          });
                          return true;
                        }
                        return false;
                      }).run();
                    }}
                    sx={{ width: 100, mx: 1 }}
                  />
                </>
              )}
            </Box>
          </BubbleMenu>
        )}
        <EditorContent editor={editor} />
      </Box>
    );
  }
);

export default TiptapEmailEditor; 